import type { GoalMode } from "./goal";
import { requireFinitePositive } from "../shared/validation";
import type { CalendarDate } from "../shared/calendar-date";

export const PROTEIN_TARGET_POLICY_VERSION = "protein-target-v1";

export const PROTEIN_GRAMS_PER_KILOGRAM: Readonly<Record<GoalMode, number>> = Object.freeze({
  gain: 1.7,
  maintain: 1.8,
  lose: 2,
});

export interface ProteinTargetResult {
  readonly policyVersion: typeof PROTEIN_TARGET_POLICY_VERSION;
  readonly gramsPerDay: number;
  readonly gramsPerKilogram: number;
  readonly referenceWeightKilograms?: number;
  readonly referenceDate?: CalendarDate;
  readonly source: "current_weight_trend" | "previous_target";
}

/** The resulting whole-gram target is fixed for every day in the next plan. */
export function calculateProteinTarget(input: {
  readonly goalMode: GoalMode;
  readonly referenceTrendWeightKilograms?: number;
  readonly referenceTrendDate?: CalendarDate;
  readonly previousDailyProteinGrams?: number;
}): ProteinTargetResult {
  const factor = PROTEIN_GRAMS_PER_KILOGRAM[input.goalMode];
  if (input.referenceTrendWeightKilograms !== undefined) {
    requireFinitePositive(input.referenceTrendWeightKilograms, "Protein reference weight");
    return {
      policyVersion: PROTEIN_TARGET_POLICY_VERSION,
      gramsPerDay: Math.round(input.referenceTrendWeightKilograms * factor),
      gramsPerKilogram: factor,
      referenceWeightKilograms: input.referenceTrendWeightKilograms,
      referenceDate: input.referenceTrendDate,
      source: "current_weight_trend",
    };
  }
  if (input.previousDailyProteinGrams !== undefined) {
    requireFinitePositive(input.previousDailyProteinGrams, "Previous protein target");
    return {
      policyVersion: PROTEIN_TARGET_POLICY_VERSION,
      gramsPerDay: Math.round(input.previousDailyProteinGrams),
      gramsPerKilogram: factor,
      source: "previous_target",
    };
  }
  throw new Error("A weight trend or previous protein target is required");
}
